/**
 * Camino día 1 del dueño (mostrador).
 * Productos: catálogo → caja (si efectivo) → primera venta.
 * Servicios (capability appointments): catálogo → primer turno. La caja no
 * bloquea el hito: el cobro del turno ocurre al atender, no al agendar.
 * Puro / unit-testeable. No mira rubros: milestone vía capability;
 * copy servicios vía flag aparte en UI.
 */

export type Day1Phase =
  | 'need_catalog'
  | 'need_cash'
  | 'need_first_sale'
  | 'need_first_appointment'
  | 'celebrate_first_sale'
  | 'celebrate_first_appointment'
  | 'done';

export type Day1Mode = 'fresh' | 'operando';

/** Hito del día 1: primera venta (productos) o primer turno (appointments). */
export type Day1Milestone = 'sale' | 'appointment';

export type Day1StepId = 'catalog' | 'cash' | 'sale' | 'appointment';

export type Day1StepStatus = 'done' | 'current' | 'upcoming' | 'skipped';

export type Day1Step = {
  id: Day1StepId;
  status: Day1StepStatus;
};

export type Day1PathInput = {
  isOwner: boolean;
  /** Dismiss del camino / celebración (`owner_checklist` storage). */
  pathDismissed: boolean;
  /**
   * Catálogo ya hidrató en este dispositivo (pull o filas locales).
   * Sin esto, 0 productos ≠ fresh (p.ej. web antes del sync).
   */
  catalogHydrated: boolean;
  /** Productos activos no-misc (Varios no cuenta). */
  nonMiscProductCount: number;
  /** Capability / módulo caja (efectivo). Solo aplica al milestone venta. */
  needsCashStep: boolean;
  cashSessionOpen: boolean;
  /** Ventas activas lifetime en el alcance (sucursal). */
  lifetimeSaleCount: number;
  /**
   * Capability appointments: el hito día 1 pasa a ser el primer turno
   * (catálogo → turno; la caja queda fuera del camino).
   */
  appointmentsEnabled?: boolean;
  /** Turnos no cancelados lifetime en el alcance (sucursal). */
  lifetimeAppointmentCount?: number;
  /** Publicación de la tienda online de la sucursal actual. Null = todavía no cargó. */
  storePublished?: boolean | null;
  /**
   * Señales de operación local (urgencias, ventas del día, pedidos…).
   * Si hay actividad, no empujar checklist de alta aunque el conteo de catálogo esté atrasado.
   */
  operationallyActive?: boolean;
};

/**
 * True mientras Inicio no debe pintar ni “operando” ni la hoja día 1.
 * No espera catálogo RAM: sin hidratar, resolveDay1Path asume fresh/need_catalog
 * (antes esperábamos sync → 4–6s de pantalla en blanco post-boot).
 */
export function isDay1HomePending(input: {
  entitlementsLoading: boolean;
  dismissLoaded: boolean;
  evidenceLoaded: boolean;
  cashBlocking: boolean;
  storePublished: boolean | null;
  hasBranch: boolean;
  isOwner: boolean;
  pathDismissed: boolean;
  catalogHydrated: boolean;
  lifetimeSaleCount: number;
  operationallyActive: boolean;
}): boolean {
  if (input.entitlementsLoading) return true;
  if (!input.dismissLoaded || !input.evidenceLoaded) return true;
  if (input.cashBlocking) return true;
  // storePublished null: pintar igual; sidebar oculta Tienda hasta saber.
  return false;
}

export function resolveDay1Milestone(input: {
  appointmentsEnabled?: boolean;
}): Day1Milestone {
  return input.appointmentsEnabled === true ? 'appointment' : 'sale';
}

/** Cantidad lifetime del hito activo (ventas o turnos según milestone). */
export function day1MilestoneCount(input: Day1PathInput): number {
  return resolveDay1Milestone(input) === 'appointment'
    ? (input.lifetimeAppointmentCount ?? 0)
    : input.lifetimeSaleCount;
}

export type Day1PathResult = {
  mode: Day1Mode;
  phase: Day1Phase;
  milestone: Day1Milestone;
  /** Pasos del camino (caja solo en milestone venta con needsCashStep). */
  steps: Day1Step[];
  /** 1-based entre pasos no skipped; 0 si done sin celebrar. */
  stepIndex: number;
  stepTotal: number;
  preferCatalogTab: boolean;
  collapseStorefrontNav: boolean;
  /**
   * Catálogo aún desconocido (sin RAM ni cache).
   * UI: skeleton del “Siguiente”, no copy falso de “Cargá productos”.
   */
  awaitingCatalog?: boolean;
};

function buildSteps(input: {
  milestone: Day1Milestone;
  includeCashStep: boolean;
  hasCatalog: boolean;
  cashOk: boolean;
  hasMilestone: boolean;
  phase: Day1Phase;
}): Day1Step[] {
  const { milestone, includeCashStep, hasCatalog, cashOk, hasMilestone, phase } = input;
  const needPhase: Day1Phase =
    milestone === 'appointment' ? 'need_first_appointment' : 'need_first_sale';
  const milestoneStepId: Day1StepId = milestone === 'appointment' ? 'appointment' : 'sale';

  const catalogStatus: Day1StepStatus = !hasCatalog
    ? phase === 'need_catalog'
      ? 'current'
      : 'upcoming'
    : 'done';

  const cashStatus: Day1StepStatus = !hasCatalog
    ? 'upcoming'
    : cashOk || hasMilestone
      ? 'done'
      : phase === 'need_cash'
        ? 'current'
        : 'upcoming';

  const milestoneStatus: Day1StepStatus = hasMilestone
    ? 'done'
    : phase === needPhase
      ? 'current'
      : 'upcoming';

  const steps: Day1Step[] = [{ id: 'catalog', status: catalogStatus }];
  if (includeCashStep) {
    steps.push({ id: 'cash', status: cashStatus });
  }
  steps.push({ id: milestoneStepId, status: milestoneStatus });
  return steps;
}

/**
 * Colapsar zona Tienda (CTA) vs menú operativo.
 * Una sola regla — usada en early-exit y en camino fresh (evita lógica duplicada / flash).
 */
export function shouldCollapseStorefrontNav(
  input: Day1PathInput,
  phase: Day1Phase | null,
): boolean {
  if (input.storePublished !== false) return false;
  if (!input.isOwner || input.pathDismissed) return false;
  if (day1MilestoneCount(input) >= 2) return false;
  if (input.operationallyActive === true) return false;

  if (
    phase === 'need_catalog' ||
    phase === 'need_cash' ||
    phase === 'need_first_sale' ||
    phase === 'need_first_appointment'
  ) {
    return true;
  }

  // Early-exit por catálogo aún no hidratado: seguimos en ventana día 1.
  // Antes devolvía collapse=false → flash de menú completo y después el CTA.
  if (!input.catalogHydrated) return true;

  return false;
}

/**
 * Resuelve fase del día 1.
 * - Empleado / dismiss → operando done.
 * - 2+ hitos → operando done (dueños ya establecidos; sin celebración eterna).
 * - 1 hito y no dismiss → celebrate.
 */
export function resolveDay1Path(input: Day1PathInput): Day1PathResult {
  const milestone = resolveDay1Milestone(input);
  const hasCatalog = input.nonMiscProductCount >= 1;
  // La caja solo es paso del camino cuando el hito es la venta.
  const includeCashStep = milestone === 'sale' && input.needsCashStep;
  const cashOk = !includeCashStep || input.cashSessionOpen;
  const milestoneCount = day1MilestoneCount(input);
  const hasMilestone = milestoneCount >= 1;
  const established = milestoneCount >= 2;
  const stepTotal = includeCashStep ? 3 : 2;
  const celebratePhase: Day1Phase =
    milestone === 'appointment' ? 'celebrate_first_appointment' : 'celebrate_first_sale';
  const needPhase: Day1Phase =
    milestone === 'appointment' ? 'need_first_appointment' : 'need_first_sale';

  const emptySteps = (): Day1Step[] =>
    buildSteps({
      milestone,
      includeCashStep,
      hasCatalog: true,
      cashOk: true,
      hasMilestone: true,
      phase: 'done',
    });

  if (
    !input.isOwner ||
    input.pathDismissed ||
    input.storePublished === true ||
    established ||
    input.operationallyActive === true
  ) {
    return {
      mode: 'operando',
      phase: 'done',
      milestone,
      steps: emptySteps(),
      stepIndex: 0,
      stepTotal,
      preferCatalogTab: false,
      collapseStorefrontNav: shouldCollapseStorefrontNav(input, null),
    };
  }

  // RAM vacía y sin conteo confiable: no mentir “Cargá productos”.
  // La UI muestra skeleton; al hidratar (o con cache) se resuelve la fase real.
  if (!input.catalogHydrated) {
    const steps = buildSteps({
      milestone,
      includeCashStep,
      hasCatalog: false,
      cashOk: false,
      hasMilestone: false,
      phase: 'need_catalog',
    });
    return {
      mode: 'fresh',
      phase: 'need_catalog',
      milestone,
      steps,
      stepIndex: 1,
      stepTotal,
      preferCatalogTab: false,
      collapseStorefrontNav: shouldCollapseStorefrontNav(input, 'need_catalog'),
      awaitingCatalog: true,
    };
  }

  let phase: Day1Phase;
  if (!hasCatalog) {
    phase = 'need_catalog';
  } else if (hasMilestone) {
    // Ya llegó al hito (aunque la caja esté cerrada: cobro con tarjeta / sin efectivo).
    phase = celebratePhase;
  } else if (!cashOk) {
    phase = 'need_cash';
  } else {
    phase = needPhase;
  }

  const steps = buildSteps({
    milestone,
    includeCashStep,
    hasCatalog,
    cashOk,
    hasMilestone,
    phase,
  });

  const currentIdx = steps.findIndex((s) => s.status === 'current');
  const stepIndex =
    phase === 'celebrate_first_sale' || phase === 'celebrate_first_appointment'
      ? stepTotal
      : currentIdx >= 0
        ? currentIdx + 1
        : 0;

  return {
    mode: 'fresh',
    phase,
    milestone,
    steps,
    stepIndex,
    stepTotal,
    preferCatalogTab: phase === 'need_catalog',
    collapseStorefrontNav: shouldCollapseStorefrontNav(input, phase),
  };
}
